import { twJoin } from "tailwind-merge"
import { ReactNode, useMemo } from "react"
import Link from "next/link"

export type SizeType = 36 | 44 | 48 | 56 | 64

export type ThemeType = "primary" | "ghost" | "secondary" | "tertiary"

export type MainButtonProps = {
  children: ReactNode
  size1440?: SizeType
  size1024?: SizeType
  size480?: SizeType
  size?: SizeType
  theme?: ThemeType
  href?: string
  target?: "_self" | "_blank" | "_parent" | "_top"
  rel?: string
  className?: string
  onClick?: () => void
}

export const MainButton = ({
  children,
  size1440,
  size1024,
  size480,
  size = 44,
  theme = "primary",
  href,
  target,
  rel,
  className,
  onClick,
}: MainButtonProps) => {
  const size1440Class = useMemo(() => {
    if (!size1440) {
      return []
    }

    switch (size1440) {
      case 48: {
        return [
          "1440:h-48p",
          "1440:text-16p",
          "1440:font-600",
          "1440:leading-20p",
          "1440:py-10p",
          "1440:px-32p",
        ]
      }
      case 56: {
        return [
          "1440:h-56p",
          "1440:text-14p",
          "1440:font-500",
          "1440:leading-20p",
          "1440:py-10p",
          "1440:px-32p",
        ]
      }
      case 64: {
        return [
          "1440:h-64p",
          "1440:text-16p",
          "1440:font-500",
          "1440:leading-20p",
          "1440:py-10p",
          "1440:px-32p",
        ]
      }
      case 36: {
        return [
          "1440:h-36p",
          "1440:text-12p",
          "1440:font-500",

          "1440:leading-20p",
          "1440:py-8p",
          "1440:px-16p",
        ]
      }
      case 44:
      default: {
        return [
          "1440:h-44p",
          "1440:text-13p",
          "1440:font-500",
          "1440:leading-20p",
          "1440:py-10p",
          "1440:px-32p",
        ]
      }
    }
  }, [size1440])

  const size1024Class = useMemo(() => {
    if (!size1024) {
      return []
    }

    switch (size1024) {
      case 48: {
        return [
          "1024:h-48p",
          "1024:text-16p",
          "1024:font-600",
          "1024:leading-20p",
          "1024:py-10p",
          "1024:px-32p",
        ]
      }
      case 56: {
        return [
          "1024:h-56p",
          "1024:text-14p",
          "1024:font-500",
          "1024:leading-20p",
          "1024:py-10p",
          "1024:px-32p",
        ]
      }
      case 64: {
        return [
          "1024:h-64p",
          "1024:text-16p",
          "1024:font-500",
          "1024:leading-20p",
          "1024:py-10p",
          "1024:px-32p",
        ]
      }
      case 36: {
        return [
          "1024:h-36p",
          "1024:text-12p",
          "1024:font-500",

          "1024:leading-20p",
          "1024:py-8p",
          "1024:px-16p",
        ]
      }
      case 44:
      default: {
        return [
          "1024:h-44p",
          "1024:text-13p",
          "1024:font-500",
          "1024:leading-20p",
          "1024:py-10p",
          "1024:px-32p",
        ]
      }
    }
  }, [size1024])

  const size480Class = useMemo(() => {
    if (!size480) {
      return []
    }
    switch (size480) {
      case 48: {
        return [
          "480:h-48p",
          "480:text-16p",
          "480:font-600",
          "480:leading-20p",
          "480:py-10p",
          "480:px-32p",
        ]
      }
      case 56: {
        return [
          "480:h-56p",
          "480:text-14p",
          "480:font-500",
          "480:leading-20p",
          "480:py-10p",
          "480:px-32p",
        ]
      }
      case 64: {
        return [
          "480:h-64p",
          "480:text-16p",
          "480:font-500",
          "480:leading-20p",
          "480:py-10p",
          "480:px-32p",
        ]
      }
      case 36: {
        return [
          "480:h-36p",
          "480:text-12p",
          "480:font-500",
          "480:leading-20p",
          "480:py-8p",
          "480:px-16p",
        ]
      }
      case 44:
      default: {
        return [
          "480:h-44p",
          "480:text-13p",
          "480:leading-20p",
          "480:font-500",
          "480:py-10p",
          "480:px-32p",
        ]
      }
    }
  }, [size480])

  const sizeClass = useMemo(() => {
    if (!size) {
      return []
    }
    switch (size) {
      case 48: {
        return [
          "h-48p",
          "text-16p",
          "font-600",
          "leading-20p",
          "py-10p",
          "px-32p",
        ]
      }
      case 56: {
        return [
          "h-56p",
          "text-14p",
          "font-500",
          "leading-20p",
          "py-10p",
          "px-32p",
        ]
      }
      case 64: {
        return [
          "h-64p",
          "text-16p",
          "font-500",
          "leading-20p",
          "py-10p",
          "px-32p",
        ]
      }
      case 36: {
        return [
          "h-36p",
          "text-12p",
          "font-500",
          "leading-20p",
          "py-8p",
          "px-16p",
        ]
      }
      case 44:
      default: {
        return [
          "h-44p",
          "text-13p",
          "font-500",
          "leading-20p",
          "py-10p",
          "px-32p",
        ]
      }
    }
  }, [size])

  const themeClass = useMemo(() => {
    switch (theme) {
      case "ghost": {
        return [
          "border-neutral-500 text-neutral2-900",
          "bg-neutral-300",
        ]
      }
      case "secondary": {
        return [
          "border-slate-blue-500 border-solid border",
          "text-slate-blue-500",
          "hover:bg-neutral2-150",
          "hover:text-neutral-600",
          "hover:border-neutral2-150",
        ]
      }
      case "tertiary": {
        return [
          "text-neutral2-700",
          "bg-white border  border-neutral2-250",
          "hover:border-slate-blue-400",
          "hover:text-slate-blue-400",
        ]
      }
      case "primary":
      default: {
        return [
          "text-white",
          "bg-slate-blue-500",
          "hover:bg-slate-blue-400",
        ]
      }
    }
  }, [theme])

  const buttonCommonStyle = twJoin(
    "whitespace-nowrap",
    "flex items-center justify-center",
    "text-center",
    "rounded-8p",
    "hover:transition-colors",
    "hover:duration-200"
  )

  if (href) {
    return (
      <Link
        onClick={onClick}
        className={twJoin(
          className,
          size1440Class,
          sizeClass,
          size480Class,
          size1024Class,
          themeClass,
          buttonCommonStyle
        )}
        href={href}
        target={target}
        rel={rel}
      >
        {children}
      </Link>
    )
  }

  return (
    <button
      onClick={onClick}
      className={twJoin(
        className,
        size1440Class,
        sizeClass,
        size480Class,
        size1024Class,
        themeClass,
        buttonCommonStyle
      )}
    >
      {children}
    </button>
  )
}
